import { endOfMonth, startOfMonth, startOfYear, subMonths } from 'date-fns';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { FILTER_TYPES, REPORT_KEYS } from '_constants/reports';
import { theme } from '_constants/theme';
import { selectFilters } from '_store/reports/selector';
import { updateFilters } from '_store/reports/slice';

import DateRangeFilter from 'components/atomic/filters/DateRangeFilter';
import MonthRangeFilter from 'components/atomic/filters/MonthRangeFilter';
import TypeFilter from 'components/atomic/filters/TypeFilter';

const defaultDateRangeFilterMap = {
  [FILTER_TYPES.dateRange]: {
    [FIELD_KEYS.dateRangeFrom]: new Date(),
    [FIELD_KEYS.dateRangeTo]: new Date(),
  },
  [FILTER_TYPES.monthToDate]: {
    [FIELD_KEYS.dateRangeFrom]: startOfMonth(new Date()),
    [FIELD_KEYS.dateRangeTo]: new Date(),
  },
  [FILTER_TYPES.lastMonth]: {
    [FIELD_KEYS.dateRangeFrom]: startOfMonth(subMonths(new Date(), 1)),
    [FIELD_KEYS.dateRangeTo]: endOfMonth(subMonths(new Date(), 1)),
  },
  [FILTER_TYPES.lastThreeMonth]: {
    [FIELD_KEYS.dateRangeFrom]: startOfMonth(subMonths(new Date(), 3)),
    [FIELD_KEYS.dateRangeTo]: endOfMonth(subMonths(new Date(), 1)),
  },
  [FILTER_TYPES.yearToDate]: {
    [FIELD_KEYS.dateRangeFrom]: startOfYear(new Date()),
    [FIELD_KEYS.dateRangeTo]: new Date(),
  },
};

const StyledContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
  margin-bottom: ${theme.spaces.largeX}px;

  > * {
    &:first-child {
      width: 180px;
    }

    &:last-child {
      flex-grow: 1;
    }
  }

  @media ${theme.devices.tablet} {
    flex-direction: column;

    > * {
      &:first-child {
        width: 100%;
      }

      &:last-child {
        width: 100%;
      }
    }
  }
`;

const Filters = () => {
  const dispatch = useDispatch();
  const expensesFilters = useSelector(selectFilters(REPORT_KEYS.expensesGraph));
  const incomeFilters = useSelector(selectFilters(REPORT_KEYS.incomeGraph));

  const handleFilterTypeSelect = (filter) => {
    const defaultDateRangeFilter = defaultDateRangeFilterMap[filter[FIELD_KEYS.filterType]] || {};

    dispatch(
      updateFilters({
        [REPORT_KEYS.expensesGraph]: {
          ...expensesFilters,
          ...filter,
          ...defaultDateRangeFilter,
        },
        [REPORT_KEYS.incomeGraph]: {
          ...incomeFilters,
          ...filter,
          ...defaultDateRangeFilter,
        },
      })
    );
  };

  const handleSelect = (filter) => {
    dispatch(
      updateFilters({
        [REPORT_KEYS.expensesGraph]: {
          ...expensesFilters,
          ...filter,
        },
        [REPORT_KEYS.incomeGraph]: {
          ...incomeFilters,
          ...filter,
        },
      })
    );
  };

  return (
    <StyledContainer>
      <TypeFilter
        name={FIELD_KEYS.filterType}
        value={expensesFilters[FIELD_KEYS.filterType]}
        onSelect={handleFilterTypeSelect}
      />
      {[FILTER_TYPES.monthToDate, FILTER_TYPES.lastMonth, FILTER_TYPES.dateRange].includes(
        expensesFilters[FIELD_KEYS.filterType]
      ) && (
        <DateRangeFilter
          nameFrom={FIELD_KEYS.dateRangeFrom}
          nameTo={FIELD_KEYS.dateRangeTo}
          valueFrom={expensesFilters[FIELD_KEYS.dateRangeFrom]}
          valueTo={expensesFilters[FIELD_KEYS.dateRangeTo]}
          onSelect={handleSelect}
        />
      )}
      {expensesFilters[FIELD_KEYS.filterType] === FILTER_TYPES.yearToDate && (
        <DateRangeFilter
          shiftByMonth
          nameFrom={FIELD_KEYS.dateRangeFrom}
          nameTo={FIELD_KEYS.dateRangeTo}
          valueFrom={expensesFilters[FIELD_KEYS.dateRangeFrom]}
          valueTo={expensesFilters[FIELD_KEYS.dateRangeTo]}
          onSelect={handleSelect}
        />
      )}
      {[FILTER_TYPES.monthRange, FILTER_TYPES.lastThreeMonth].includes(expensesFilters[FIELD_KEYS.filterType]) && (
        <MonthRangeFilter
          nameFrom={FIELD_KEYS.dateRangeFrom}
          nameTo={FIELD_KEYS.dateRangeTo}
          valueFrom={expensesFilters[FIELD_KEYS.dateRangeFrom]}
          valueTo={expensesFilters[FIELD_KEYS.dateRangeTo]}
          onSelect={handleSelect}
        />
      )}
    </StyledContainer>
  );
};

export default Filters;
